import { Component, Input, OnInit } from '@angular/core';
import { DashboardService } from 'src/app/dashboard/dashboard.service';
import { CvDialogService } from 'src/app/reusables/confirm-dialog/cv-dialog.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { WorkspaceNewTabViewMode, WorkspaceTemplateViewMode } from 'src/shared/models/dashboard.model';

@Component({
    selector: 'cv-create-new-dashboard',
    templateUrl: './create-new-dashboard.component.html',
    styleUrls: ['./create-new-dashboard.component.scss']
})
export class CreateNewDashboardComponent implements OnInit {
    @Input() workspaceKey: string;
    @Input() fromTemplate = false;

    newDashboard = {
        name: '',
        group: '',
        description: '',
    };

    isSaving = false;


    constructor(
        public dashboardService: DashboardService,
        private cvLocalizationService: CVLocalizationService,
        private dialogService: CvDialogService,
    ) {

    }

    ngOnInit() {
        this.cvLocalizationService.GetSection("Left_Nav_Popup");

        if (this.fromTemplate) {
            const sharedTemplate = this.dashboardService.selectedTemplate;
            this.newDashboard.description = "Created from template " + sharedTemplate.DashboardName;
            this.newDashboard.name = sharedTemplate.DashboardName;
            this.newDashboard.group = sharedTemplate.DashboardGroup;
            this.dashboardService.newDashboardPreviousView = WorkspaceNewTabViewMode.Templates;
        }
    }

    get canSave() {
        return this.newDashboard.name
            && this.newDashboard.group
            && !(this.newDashboard.group.toLowerCase() === 'taskbar')
            && !this.isSaving;
    }

    get saveButtonTooltip() {
        if (this.newDashboard.group.toLowerCase() === 'taskbar') {
            return this.cvLocalizationService.getCachedText(
                "Left_Nav_Popup",
                "Taskbar_Reserved",
                '"Taskbar" is a reserved group name. Please use a different group name.'
            );
        } else if (this.newDashboard.name === '' || this.newDashboard.group === '') {
            return this.cvLocalizationService.getCachedText(
                "Left_Nav_Popup",
                "Name_And_Group_Required",
                "Dashboard name and group are required fields."
            );
        } else {
            return "";
        }
    }

    async save() {
        if (!this.canSave)
            return;

        if (this.newDashboard.group === 'Shared With Me') {
            const warningTitle = this.cvLocalizationService.getCachedText("Left_Nav_Popup", "Warning", "Warning");
            const warningMessage = this.cvLocalizationService.getCachedText(
                "Left_Nav_Popup",
                "Folder_Restricted",
                "This folder is restricted for Shared Dashboards only. Please select a different folder."
            );
            this.dialogService.showWarningDialog(warningTitle, warningMessage);
            return;
        }

        if (this.fromTemplate) {
            await this.saveTemplate();
            return;
        }

        // save dashboard
        this.isSaving = true;
        try {
            const dashboard = await this.dashboardService.createNewDashboard(
                this.workspaceKey,
                this.newDashboard.name,
                this.newDashboard.group,
                this.newDashboard.description
            );

            // refresh dashboard list
            this.dashboardService.loadExistingDashboardsAndGroups();
        } finally {
            this.isSaving = false;
            this.dashboardService.newTabSelectionView = WorkspaceNewTabViewMode.Landing;
        }
    }

    async saveTemplate() {
        this.isSaving = true;
        const sharedTemplate = this.dashboardService.selectedTemplate;
        this.dashboardService.newTemplateDetails.dashboardName = this.newDashboard.name;
        this.dashboardService.newTemplateDetails.dashboardGroup = this.newDashboard.group;
        this.dashboardService.newTemplateDetails.dashboardDescription = this.newDashboard.description;
        this.dashboardService.newTemplateDetails.readyToBeSubmitted = true;

        try {
            const dashboard = await this.dashboardService.copyDashboardFromTemplate(
                sharedTemplate.DashboardKey,
                sharedTemplate.RequestingLoginID,
                this.newDashboard.name,
                this.newDashboard.group,
                this.newDashboard.description
            );

            this.dashboardService.newTabSelectionView = WorkspaceNewTabViewMode.Landing;
            this.dashboardService.templateView = WorkspaceTemplateViewMode.Landing;
            this.dashboardService.selectedTemplate = null;
            this.dashboardService.loadExistingDashboardsAndGroups();
            this.dashboardService.addDashboardToWorkspace(this.workspaceKey, dashboard, true);
        } finally {
            this.isSaving = false;
        }
    }

    cancel() {
        if (this.dashboardService.newDashboardPreviousView === WorkspaceNewTabViewMode.Existing) {
            this.dashboardService.templateView = WorkspaceTemplateViewMode.Landing;
            this.dashboardService.newDashboardPreviousView = WorkspaceNewTabViewMode.Landing;
            this.dashboardService.newTabSelectionView = WorkspaceNewTabViewMode.Existing;
        }
        else if (this.dashboardService.newDashboardPreviousView === WorkspaceNewTabViewMode.Templates) {
            this.dashboardService.newTabSelectionView = WorkspaceNewTabViewMode.Templates;
            this.dashboardService.templateView = WorkspaceTemplateViewMode.Landing;
            this.dashboardService.newDashboardPreviousView = WorkspaceNewTabViewMode.Landing;
        }
        else
            this.dashboardService.newTabSelectionView = WorkspaceNewTabViewMode.Landing;
    }
}
